import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext();

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const LONG_ABSENCE_THRESHOLD = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', Date.now().toString());

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer only if user is logged in
    if (token && user) {
      inactivityTimerRef.current = setTimeout(() => {
        // Check if they've been away for more than 3 hours
        const lastActivity = localStorage.getItem('lastActivity');
        const timeSinceLastActivity = lastActivity ? Date.now() - parseInt(lastActivity) : 0;
        const shouldSavePath = timeSinceLastActivity < LONG_ABSENCE_THRESHOLD;
        
        if (shouldSavePath) {
          toast.info('Session expired due to inactivity. Please log in again.');
        } else {
          toast.info('Welcome back! Your session has expired.');
        }
        
        logout(shouldSavePath);
      }, INACTIVITY_TIMEOUT);
    }
  }, [token, user]);

  // Check for expired session on mount
  useEffect(() => {
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity && token) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
      if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
        // Session expired - only save path if they've been away for less than 3 hours
        const shouldSavePath = timeSinceLastActivity < LONG_ABSENCE_THRESHOLD;
        logout(shouldSavePath);
        setLoading(false);
        return;
      }
    }

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Set up activity listeners
  useEffect(() => {
    if (token && user) {
      // Events that indicate user activity
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
      
      events.forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
      });

      // Initialize timer
      resetInactivityTimer();

      // Cleanup
      return () => {
        events.forEach(event => {
          window.removeEventListener(event, resetInactivityTimer);
        });
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
      };
    }
  }, [token, user, resetInactivityTimer]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Don't save path on auth failure - token is invalid
      logout(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('lastActivity', Date.now().toString());
    setToken(token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return user;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/auth/register`, { name, email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = (saveCurrentPath = false) => {
    // Save current path if requested (for session timeout scenarios)
    if (saveCurrentPath && window.location.pathname !== '/login') {
      localStorage.setItem('returnUrl', window.location.pathname);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);